import { useCallback, useRef } from "react";
import { hasTextNodes } from "../utils/iframeHelpers";
import {
  injectEditorStyles,
  morphIframeDocument,
  resetEditableElements,
} from "../services/editor";

export function useIframeEvents(iframeRef, callbacks) {
  const {
    onElementClick,
    onLinkClick,
    onImageClick,
    onScroll,
    onKeyUp,
    onEscape,
    markChanged,
  } = callbacks;

  const boundRef = useRef(new WeakSet());
  const restoreScrollRef = useRef(null);

  // For a reload that should land where the user was; the scroll is put back on load
  const keepScrollAcrossReload = useCallback(() => {
    restoreScrollRef.current = iframeRef.current.contentWindow.scrollY;
  }, [iframeRef]);

  // Idempotent: runs on load and again after every in-place patch, binding only new elements.
  const bindElementListeners = useCallback(
    (iframeDoc) => {
      const bind = (element, listener) => {
        if (boundRef.current.has(element)) return;
        boundRef.current.add(element);
        element.addEventListener("click", listener);
      };

      iframeDoc.querySelectorAll("img").forEach((image) => {
        image.removeAttribute("srcset");
        bind(image, (e) => {
          e.preventDefault();
          e.stopPropagation();
          markChanged?.();
          onImageClick?.(image, e);
        });
      });

      iframeDoc.querySelectorAll("a").forEach((link) => {
        bind(link, (e) => {
          e.preventDefault();
          e.stopPropagation();
          markChanged?.();

          resetEditableElements(iframeDoc);
          if (link.innerText !== "") {
            link.contentEditable = true;
          }
          link.classList.add("tiinyeditabletext", "tiinyedit");

          const children = link.children;
          for (let i = 0; i < children.length; i++) {
            children[i].setAttribute("contenteditable", "true");
          }
          link.focus();

          onLinkClick?.(link, e);
        });
      });

      iframeDoc.querySelectorAll("button").forEach((button) => {
        bind(button, (e) => {
          e.preventDefault();
          e.stopPropagation();
          e.target.contentEditable = true;
          e.target.focus();
          markChanged?.();
        });
      });
    },
    [markChanged, onImageClick, onLinkClick],
  );

  const setupEventListeners = useCallback(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    const iframeDoc = iframe.contentWindow.document;
    const iframeWindow = iframe.contentWindow;

    const createElementClickEvent = (event, target = event.target) => ({
      type: event.type,
      target,
      currentTarget: event.currentTarget,
      clientX: event.clientX,
      clientY: event.clientY,
      preventDefault: () => event.preventDefault(),
      stopPropagation: () => event.stopPropagation(),
    });

    // Inject editor styles
    injectEditorStyles(iframeDoc);

    if (restoreScrollRef.current !== null) {
      iframeWindow.scrollTo(0, restoreScrollRef.current);
      restoreScrollRef.current = null;
    }

    // Mouseover - highlight editable elements
    iframeDoc.addEventListener("mouseover", (e) => {
      if (e.target.tagName.toLowerCase() === "img") {
        e.target.classList.add("tiinyeditableimage");
      } else if (hasTextNodes(e.target)) {
        e.target.classList.add("tiinyeditabletext");

        // Add activation handler only once per element
        if (
          !["a", "button"].includes(e.target.tagName.toLowerCase()) &&
          !e.target.dataset.tiinyClickHandler
        ) {
          e.target.dataset.tiinyClickHandler = "true";
          e.target.addEventListener("mousedown", (event) => {
            const target = event.currentTarget;
            const activeAncestor = target.parentElement?.closest(".tiinyedit");
            if (activeAncestor && target !== activeAncestor) {
              return;
            }

            resetEditableElements(iframeDoc);
            target.contentEditable = true;
            target.setAttribute("contenteditable", "true");
            target.classList.add(
              "tiinyeditabletext",
              "tiinyedit",
              "tiinyeditablefocused",
            );

            const children = target.children;
            for (let i = 0; i < children.length; i++) {
              children[i].setAttribute("contenteditable", "true");
            }

            target.focus({ preventScroll: true });

            onElementClick?.(createElementClickEvent(event, target));
          });
        }
      } else if (!e.target.dataset.tiinyClickHandler) {
        e.target.dataset.tiinyClickHandler = "true";
        e.target.addEventListener("click", (event) => {
          const activeEditable = iframeDoc.querySelector(".tiinyedit");
          if (activeEditable && activeEditable.contains(event.target)) {
            return;
          }

          resetEditableElements(iframeDoc);
          onScroll?.();
        });
      }
    });

    // Mouseout - remove highlights
    iframeDoc.addEventListener("mouseout", (e) => {
      if (e.target.tagName.toLowerCase() === "img") {
        e.target.classList.remove("tiinyeditableimage", "tiinyeditablefocused");
      } else if (!e.target.classList.contains("tiinyedit")) {
        e.target.classList.remove("tiinyeditabletext", "tiinyeditablefocused");
      }
    });

    bindElementListeners(iframeDoc);

    // Scroll - hide panel
    iframeWindow.addEventListener("scroll", () => {
      onScroll?.();
    });

    // Keyup - content changed
    iframeDoc.addEventListener("keyup", (e) => {
      e.stopPropagation();
      onKeyUp?.(e);
    });

    // Escape - reset
    iframeDoc.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        resetEditableElements(iframeDoc);
        onEscape?.();
      }
    });

    // Click - scroll to element in editor
    iframeDoc.addEventListener("click", (e) => {
      const activeEditable = iframeDoc.querySelector(".tiinyedit");
      if (activeEditable && activeEditable.contains(e.target)) {
        return;
      }

      onElementClick?.(createElementClickEvent(e));
    });
  }, [
    iframeRef,
    bindElementListeners,
    onElementClick,
    onScroll,
    onKeyUp,
    onEscape,
  ]);

  const applyPreviewHtml = useCallback(
    (html) => {
      const iframeDoc = iframeRef.current.contentWindow.document;
      morphIframeDocument(iframeDoc, html);
      bindElementListeners(iframeDoc);
    },
    [iframeRef, bindElementListeners],
  );

  const resetDocument = useCallback(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    resetEditableElements(iframe.contentWindow.document);
  }, [iframeRef]);

  return {
    setupEventListeners,
    applyPreviewHtml,
    keepScrollAcrossReload,
    resetDocument,
  };
}
